import {
  CustomFormSchemaFieldsRecord,
  customFormSchemaFieldsFromJSON,
  customFormSchemaFieldsToJSON,
  ICustomFormSchemaFieldsData,
} from "common/records/CustomFormSchemaFields";
import { List, Map, Record } from "immutable";
import { JSONSchema7 } from "json-schema";

export const customFormSchemaFromJSON = (
  json: Partial<ICustomFormSchemaData>,
  uiOrder: List<string> = List(),
): CustomFormSchemaRecord => {
  const record: ICustomFormSchema = { ...(json as any) };

  // Convert the JS object to a List
  const tmpProperties = Map<string, ICustomFormSchemaFieldsData>(json.properties || {});
  const propertiesList = uiOrder.map((key) => tmpProperties.get(key)).filter((f) => f !== undefined);

  const notInUiOrderKeys = tmpProperties.keySeq().filter((key) => !uiOrder.includes(key));
  const notInUIOrder = notInUiOrderKeys.map((key) => tmpProperties.get(key)).filter((f) => f !== undefined);

  record.properties = propertiesList.concat(notInUIOrder).map((d) => customFormSchemaFieldsFromJSON(d));

  return new CustomFormSchemaRecord(record);
};

export const customFormSchemaToJSON = (record: CustomFormSchemaRecord): Partial<ICustomFormSchemaData> => {
  const json = record.toJS() as Partial<ICustomFormSchemaData>;

  const allKeys = Object.keys(json);
  for (const key of allKeys) {
    if (json[key] !== undefined) {
      continue;
    }

    delete json[key];
  }

  // Convert the List back to a JS object
  const propertiesJS: { [name: string]: ICustomFormSchemaFieldsData } = {};
  record.properties?.map((p) => {
    const pData = customFormSchemaFieldsToJSON(p);
    if (pData.fieldName) {
      propertiesJS[pData.fieldName] = pData;
    }
  });

  json.properties = propertiesJS;

  return json;
};

export interface ICustomFormSchemaData extends Omit<JSONSchema7, "properties"> {
  /** OCC-specific overrides are included in values of this object. */
  properties?: { [name: string]: ICustomFormSchemaFieldsData };

  // Preserves original functionality
  [name: string]: any;
}

export interface ICustomFormSchema extends Omit<JSONSchema7, "properties"> {
  /** OCC-specific overrides are included in values of this object. */
  properties?: List<CustomFormSchemaFieldsRecord>;

  // Preserves original functionality
  [name: string]: any;
}

export const defaultCustomFormSchemaProps: ICustomFormSchema = {
  properties: List<CustomFormSchemaFieldsRecord>(),

  // JSONSchema7 fields
  $comment: undefined,
  $defs: undefined,
  $id: undefined,
  $ref: undefined,
  $schema: undefined,
  additionalItems: undefined,
  additionalProperties: undefined,
  allOf: undefined,
  anyOf: undefined,
  const: undefined,
  contains: undefined,
  contentEncoding: undefined,
  contentMediaType: undefined,
  default: undefined,
  definitions: undefined,
  dependencies: undefined,
  description: undefined,
  else: undefined,
  enum: undefined,
  examples: undefined,
  exclusiveMaximum: undefined,
  exclusiveMinimum: undefined,
  format: undefined,
  if: undefined,
  items: undefined,
  maximum: undefined,
  maxItems: undefined,
  maxLength: undefined,
  maxProperties: undefined,
  minimum: undefined,
  minItems: undefined,
  minLength: undefined,
  minProperties: undefined,
  multipleOf: undefined,
  not: undefined,
  oneOf: undefined,
  pattern: undefined,
  patternProperties: undefined,
  propertyNames: undefined,
  readOnly: undefined,
  required: undefined,
  then: undefined,
  title: undefined,
  type: undefined,
  "ui:order": undefined,
  uniqueItems: undefined,
  writeOnly: undefined,
};

export class CustomFormSchemaRecord
  extends Record<ICustomFormSchema>(defaultCustomFormSchemaProps)
  implements ICustomFormSchema {}
